/**
 * Demo-mode suppression state.
 *
 * Demo mode forces every client-evaluated feature flag off so nothing
 * half-finished is on screen while a clinician audience is watching. The state
 * lives in a module-level store rather than React context for two reasons:
 *
 *  - `useFeatureFlag` is the consumer, and it is called both above and below
 *    every plausible provider mount point (`AuthenticatedLayout` down to
 *    individual form fields). A store has no placement problem.
 *  - The persisted preference is readable synchronously at module load, so
 *    suppression is in force on the very first paint. Waiting for a provider
 *    effect would let a flagged surface flash on screen — the one thing demo
 *    mode exists to prevent.
 *
 * Eligibility (ICT + the `demoMode` flag) is NOT decided here; `useDemoMode`
 * owns that and clears the state for anyone who turns out to be ineligible.
 *
 * @agentic connections
 */

/**
 * Demo-mode preference localStorage key.
 * @agentic connections
 */
const DEMO_MODE_KEY = "connections.demoMode";

let active = readDemoModePreference();

const listeners = new Set<() => void>();

/**
 * Subscribes to demo-mode changes. Returns the unsubscribe function, matching
 * the `useSyncExternalStore` contract.
 * @agentic connections
 */
export const subscribeToDemoMode = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Whether demo mode is currently suppressing feature-flagged surfaces.
 * @agentic connections
 */
export const isDemoModeActive = (): boolean => active;

/**
 * Sets demo mode and persists the preference. No-ops when unchanged so
 * subscribers aren't woken for nothing.
 * @agentic connections
 */
export const setDemoModeActive = (next: boolean): void => {
  if (active === next) {
    return;
  }
  active = next;
  writeDemoModePreference(next);
  for (const listener of listeners) {
    listener();
  }
};

/**
 * Reads the persisted preference, defaulting to off.
 *
 * Web Storage throws (rather than returning null) when the browser blocks it —
 * Chrome with all cookies blocked, some enterprise policies. This module is
 * imported by `useFeatureFlag`, so an unguarded read would take down every
 * screen in the app over a presentation preference.
 * @agentic connections
 */
function readDemoModePreference(): boolean {
  try {
    return window.localStorage.getItem(DEMO_MODE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Persists the preference, tolerating blocked or full storage. Demo mode still
 * works for the current session when the write fails; it just won't survive a
 * reload.
 * @agentic connections
 */
function writeDemoModePreference(enabled: boolean): void {
  try {
    window.localStorage.setItem(DEMO_MODE_KEY, String(enabled));
  } catch {
    // Best-effort; the in-memory value is the source of truth.
  }
}
